import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@tvg/fuse/button';
import saveForLaterService from 'services/save-for-later/save-for-later-service';
import sendAnalyticEvent from 'services/analytic/analytic-service';
import appWebhook from '@sd-utility/appwebhook';
import { IconFavoriteFilled, IconFavorite } from '@tvg/fuse/icon/all';
import eventBus from '@sd-utility/eventbus';
import { ProductVariant } from 'types/Product';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { CI_LIB_VERSION } from '@very-libs/customer-interaction-event-types';
import ErrorDialog from '../ErrorDialog/ErrorDialog';
import styles from './SaveForLater.module.css';

type Props = {
  productId: string;
  itemNumber: string;
  sku?: ProductVariant;
  mockEnabled: boolean;
};

const loggerSFL = LoggerFactory.getLogger('Save for later component');

// Retries capturing an error in Elastic APM until the RUM agent has loaded (max 10 attempts, 500ms apart)
const captureApmError = (error: unknown, labels: Record<string, string>, errorCode: string, retryCount = 0) => {
  const apmError = error instanceof Error ? error : new Error(String(error || 'Unknown error'));
  const apm = window.elasticApm;

  if (apm?.captureError) {
    apm.addLabels?.({
      ...labels,
      errorCode,
    });
    apm.captureError(apmError);
    return;
  }

  if (retryCount < 10) {
    setTimeout(() => {
      captureApmError(error, labels, errorCode, retryCount + 1);
    }, 500);
  } else {
    // Log that we couldn't capture the error after retries
    console.warn('[APM] Failed to capture error after 10 retries:', errorCode);
  }
};

// TODO DC-5620: Custom transactions (startTransaction/end) are being cancelled by Celebrus XHR interception
// on click events. Similar issue exists in Container.tsx for view_event.
// Ticket DC-5620 will implement success event tracking using APM's viewEvent API instead,
// which uses a different transport mechanism that doesn't conflict with Celebrus.
// Relevant events to track in SaveForLater:
// - "Add To Wishlist Event Pulse - success"
// - "Save For Later - Add - success"
// - "Save For Later - Remove - success"

function SaveForLater(props: Props) {
  const { productId, itemNumber, sku, mockEnabled } = props;
  const [isProductExist, setIsProductExists] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [dialogRendered, setDialogRendered] = useState(false);
  const isFirstRender = useRef(true);
  const hasFetched = useRef(false);

  const handleProductExistsCall = async () => {
    try {
      const isProductExisting = await saveForLaterService.checkProductExistsInSaveForLater(
        mockEnabled,
        productId,
        sku?.option || productId,
      );
      setIsProductExists(isProductExisting.productExists);
    } catch (error) {
      (await loggerSFL).logWithCode(ErrorCodes.SAVE_FOR_LATER_EXISTING_ITEM, error);
      console.error(error);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (dialogRendered) {
      setHasError(true);
    }
  }, [dialogRendered]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    handleProductExistsCall();
  }, [productId]);

  const openErrorDialog = () => {
    if (dialogRendered) {
      setHasError(true);
    } else {
      setDialogRendered(true);
    }

    sendAnalyticEvent({
      gaAction: 'Save For Later',
      gaLabel: 'Ajax Failure - AddProduct',
    });
  };

  const handleAppWebHook = (isAdd: boolean) => appWebhook('saveForLater', { action: isAdd ? 'added' : 'removed' });

  const getProductDataFromODM = () => {
    try {
      if (window.ODM && window.ODM.page && window.ODM.page.data) {
        const productData = window.ODM.page.data.get({ domain: 'product', description: 'display' });
        return productData?.product;
      }
      return null;
    } catch (error) {
      console.error('Failed to get product data from ODM:', error);
      return null;
    }
  };

  const getCustomerIdFromODM = async () => {
    try {
      if (window.ODM && window.ODM.customer && typeof window.ODM.customer.getCustomer === 'function') {
        const customerResponse = await window.ODM.customer.getCustomer();
        return customerResponse?.data?.customerID || 'default';
      }
      return 'default';
    } catch (error) {
      console.error('Failed to get customer data from ODM:', error);
      return 'default';
    }
  };

  const publishAddToWishlistEvent = async (skuOption: string) => {
    try {
      const productData = getProductDataFromODM();

      if (!productData) {
        (await loggerSFL).logWithCode(
          ErrorCodes.SAVE_FOR_LATER_EXISTING_ITEM,
          new Error('Product data not available from ODM'),
        );
        return;
      }

      const customerId = await getCustomerIdFromODM();
      const unitSalePrice = typeof productData.unit_sale_price === 'number' ? productData.unit_sale_price : 0;
      const unitPrice = typeof productData.unit_price === 'number' ? productData.unit_price : unitSalePrice;
      const stockMessage = productData.stockMessage || sku?.stock?.primary || '';

      const addToWishlistEvent = {
        eventType: 'analytics/add_to_wishlist',
        version: CI_LIB_VERSION,
        eventGroup: 'Scenario',
        ecommerce: {
          currency: productData.currency as 'GBP' | 'EUR',
          items: [
            {
              index: 1,
              itemLineNum: itemNumber,
              itemName: productData.name || '',
              itemPrice: unitSalePrice,
              itemSkuNum: skuOption,
              itemWasPrice: unitPrice,
              productGroupID: String(productData.productGroupId || productId),
              itemCategory: productData.category || '',
              itemColour: sku?.options?.colour || '',
              itemDepartment: productData.department || '',
              itemManufacturer: productData.manufacturer || '',
              itemRating: productData.rating || undefined,
              itemRatingCnt: productData.ratingCount || undefined,
              itemSize: sku?.options?.size || '',
              itemSubCategory: productData.subcategory || '',
              itemUrl: window.location.href,
              wishlistID: customerId,
              itemStockMessage: stockMessage,
            },
          ],
          value: unitSalePrice,
          wishlistID: customerId,
          wishlistName: 'default',
          referrer: 'PDP',
        },
      };

      eventBus.publish('analytics/add_to_wishlist', addToWishlistEvent);

      (await loggerSFL).info('add_to_wishlist event published', {
        productId,
        sku: skuOption,
        eventType: 'analytics/add_to_wishlist',
      });
    } catch (error) {
      (await loggerSFL).logWithCode(
        ErrorCodes.SAVE_FOR_LATER_SERVICE_ERROR,
        error instanceof Error ? error : new Error('Failed to publish add_to_wishlist event'),
        {
          productId,
          sku: sku?.option,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        },
      );

      captureApmError(error, { productGroupId: productId, sku: skuOption }, 'Add To Wishlist Event Pulse - failed');
    }
  };

  const handleSaveForLaterToggle = async () => {
    try {
      if (isProductExist) {
        const removeFromSaveForLaterList = await saveForLaterService.removeProductFromSaveForLater(
          mockEnabled,
          sku?.option || productId,
        );
        if (removeFromSaveForLaterList) {
          handleAppWebHook(false);
          sendAnalyticEvent({
            gaAction: 'Click',
            gaLabel: 'Save for later - Remove',
          });

          eventBus.publish('wishlist/remove', {
            id: productId,
            itemNumber,
            sku: sku?.option,
          });

          setIsProductExists(false);
        }
      } else {
        const addToSaveForLaterList = await saveForLaterService.addProductToSaveForLater(
          mockEnabled,
          sku?.option || productId,
        );
        if (addToSaveForLaterList) {
          handleAppWebHook(true);
          sendAnalyticEvent({
            gaAction: 'Click',
            gaLabel: 'Save for later',
          });

          eventBus.publish('wishlist/add', {
            id: productId,
            itemNumber,
            sku: sku?.option,
          });

          // Publish analytics/add_to_wishlist event
          await publishAddToWishlistEvent(sku?.option || productId);

          setIsProductExists(true);
        }
      }
    } catch (error) {
      (await loggerSFL).logWithCode(ErrorCodes.SAVE_FOR_LATER_SERVICE_ERROR, error);

      captureApmError(
        error,
        { productGroupId: productId, sku: sku?.option || productId },
        'Save For Later Toggle - failed',
      );

      openErrorDialog();
    }
  };

  const closeErrorDialog = () => setHasError(false);

  const handleErrorCancel = () => {
    sendAnalyticEvent({
      gaAction: 'Save For Later - Error Dialog',
      gaLabel: 'Cancel',
    });
  };

  const handleErrorClose = () => {
    sendAnalyticEvent({
      gaAction: 'Save For Later - Error Dialog',
      gaLabel: 'Close',
    });
  };

  const handleErrorReload = () => {
    sendAnalyticEvent({
      gaAction: 'Save For Later - Error Dialog',
      gaLabel: 'Reload',
    });
  };

  return (
    <>
      <Button
        variant="tertiary"
        onClick={handleSaveForLaterToggle}
        data-cnstrc-btn="add_to_wishlist"
        data-testid="saved-for-later-icon-container"
        size="medium"
        fullWidth
        className={styles.button}
        icon={
          isProductExist ? (
            <IconFavoriteFilled
              height="20px"
              width="20px"
              title="filled heart"
              desc="filled heart for save for later"
            />
          ) : (
            <IconFavorite title="empty heart" desc="empty heart for save for later" height="20px" width="20px" />
          )
        }
      >
        {isProductExist ? 'Item saved' : 'Save item'}
      </Button>

      {dialogRendered && (
        <ErrorDialog
          open={hasError}
          closeDialog={closeErrorDialog}
          onCancelCallback={handleErrorCancel}
          onCloseCallback={handleErrorClose}
          onReloadCallback={handleErrorReload}
        />
      )}
    </>
  );
}

export default SaveForLater;
